import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import backofficeApiClient, {
  AssignmentPrimaryDocumentEditModel,
  CreatePartnerModel,
  PartnerResponseModel,
  SplitTypeEnum,
  PartnerStatusGroupFilterEnum,
} from 'api/backofficeApiClient';
import adminApiClient, { UpdateIsIcpRequestModel } from 'api/adminApiClient';
import { createNewAssignment } from 'redux/reducers/assignmentSlice';

interface PartnerState {
  partnerCreatedAt?: number;
  allPartners: PartnerResponseModel[];
}

const initialState: PartnerState = {
  partnerCreatedAt: undefined,
  allPartners: [],
};

const createPartner = createAsyncThunk(
  'partner/createPartner',
  async (data: CreatePartnerModel) => {
    return await backofficeApiClient.createNewPartner(data);
  },
);

const getPartners = createAsyncThunk(
  'partner/getPartners',
  async ({
    pageNumber,
    isRegisteredOnMP,
    companyIds,
  }: {
    pageNumber: number;
    isRegisteredOnMP?: boolean; // needed to be provided for admin
    companyIds?: string[]; // needed to be provided for admin
  }) => {
    return adminApiClient.getPartners(pageNumber, isRegisteredOnMP, companyIds);
  },
);

const getPartnerRequestsByStatusGroup = createAsyncThunk(
  'partner/getPartnerRequestsByStatusGroup',
  async ({
    pageNumber,
    statusGroup,
  }: {
    pageNumber: number;
    statusGroup: PartnerStatusGroupFilterEnum;
  }) => {
    return backofficeApiClient.getPartnerRequestsByStatusGroup(
      pageNumber,
      statusGroup,
    );
  },
);

const getAllPartners = createAsyncThunk('partner/getAllPartners', async () => {
  return backofficeApiClient.getAllMyPartnerRequests();
});

const getAllPartnersByMember = createAsyncThunk(
  'partner/getAllPartnersByMember',
  async (memberId: string) => {
    return adminApiClient.getPartnerListForMember(memberId);
  },
);

interface PartnerDetailsModel {
  partnerId: string;
  isAdminRole?: boolean;
}

const getPartnerDetails = createAsyncThunk(
  'partner/getPartnerDetails',
  async ({ partnerId, isAdminRole }: PartnerDetailsModel) => {
    return isAdminRole
      ? adminApiClient.getPartnerInfo(partnerId)
      : backofficeApiClient.getPartnerDetail(partnerId);
  },
);

interface UpdatePartnerIsIcpOptions {
  partnerId: string;
  isIcp: boolean;
}

const updatePartnerIsIcp = createAsyncThunk(
  'partner/updatePartnerIsIcp',
  async ({ partnerId, isIcp }: UpdatePartnerIsIcpOptions) => {
    const requestBody: UpdateIsIcpRequestModel = { isIcp };
    return await adminApiClient.updatePartnerIsIcp(partnerId, requestBody);
  },
);

const getMembersWithPartners = createAsyncThunk(
  'partner/getMembersWithPartners',
  async (isRegisteredOnMP: boolean) => {
    return adminApiClient.getUsersWithPartnersData(isRegisteredOnMP);
  },
);

interface InvitePartnerParams {
  invitationId: string;
  accept: boolean;
}

const getInvitePartnerExternalData = createAsyncThunk(
  'partner/getInvitePartnerExternalData',
  async ({ invitationId, accept }: InvitePartnerParams) => {
    return accept
      ? backofficeApiClient.acceptExistingPartnerInvitation(invitationId)
      : backofficeApiClient.getRegistrationDataForExternalPartnerInvitation(
          invitationId,
        );
  },
);

const setNewPartnerCreatedAt = (state: PartnerState) => {
  state.partnerCreatedAt = Date.now();
};

const handleAssignmentCreate = (
  state: PartnerState,
  data: AssignmentPrimaryDocumentEditModel,
) => {
  if (data.splitInfo?.splitType !== SplitTypeEnum.NewPartner) return;

  setNewPartnerCreatedAt(state);
};

const partnerSlice = createSlice({
  name: 'partner',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllPartners.fulfilled, (state, action) => {
      state.allPartners = action.payload;
    });
    builder.addCase(createPartner.fulfilled, (state) => {
      setNewPartnerCreatedAt(state);
    });
    builder.addCase(createNewAssignment.fulfilled, (state, action) => {
      handleAssignmentCreate(state, action.meta.arg);
    });
  },
});

const { reducer } = partnerSlice;

export {
  createPartner,
  getPartners,
  getAllPartners,
  getAllPartnersByMember,
  getPartnerDetails,
  updatePartnerIsIcp,
  getMembersWithPartners,
  getInvitePartnerExternalData,
  getPartnerRequestsByStatusGroup,
};
export default reducer;
